import React, { useCallback, useEffect, useState } from 'react';
import { Store, Truck, Pencil, Printer, CheckCircle, Clock, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fmtNum, formatFechaHora } from '@/lib/recuentos';
import { ExportJustoButton } from '@/components/ExportJustoButton';
import { consolidadoAPI, DespachoConsolidadoDialog } from './DespachoConsolidadoDialog';

const DespachoHecho = ({ d, onEdit }) => (
  <div className="flex flex-wrap items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2" data-testid={`consol-despacho-${d.id}`}>
    <CheckCircle className="w-4 h-4 text-green-600" />
    <div className="flex-1 min-w-[160px] text-xs">
      <div className="font-semibold text-green-800">Despacho N° {d.numero} · {d.total_items} {d.total_items === 1 ? 'producto' : 'productos'} · {fmtNum(d.total_dispatched)} unid.</div>
      <div className="text-green-700/80">{formatFechaHora(d.dispatched_at)} · {d.dispatched_by_name}{d.orders_detail?.length ? ` · ${d.orders_detail.map((o) => o.maestro).join(', ')}` : ''}</div>
    </div>
    <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`consol-editar-${d.id}`}><Pencil className="w-4 h-4" /></Button>
    <ExportJustoButton tipo="dispatch" id={d.id} />
  </div>
);

const LocalCard = ({ g, onStart, onEdit, onPrint }) => {
  const p = g.pendiente;
  const [verTodos, setVerTodos] = useState(false);
  return (
    <div className={`bg-white rounded-xl border-2 p-5 space-y-3 ${p ? 'border-amber-200' : 'border-green-200 bg-green-50/30'}`} data-testid={`consol-card-${g.location_id}`}>
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${p ? 'bg-primary/10' : 'bg-green-100'}`}>
          <Store className={`w-6 h-6 ${p ? 'text-primary' : 'text-green-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900">{g.location_name}</h3>
          <p className="text-xs text-muted-foreground">{p ? `${p.orders_detail.length} ${p.orders_detail.length === 1 ? 'maestro' : 'maestros'}: ${p.orders_detail.map((o) => o.maestro).join(', ')}` : 'Todo despachado'}</p>
        </div>
        {p ? <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700"><Clock className="w-3.5 h-3.5 mr-1" />Pendiente</span>
          : <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700"><CheckCircle className="w-3.5 h-3.5 mr-1" />Despachado</span>}
      </div>
      {p && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-slate-900" data-testid={`consol-productos-${g.location_id}`}>{p.total_productos}</p><p className="text-xs text-muted-foreground">productos</p></div>
            <div className="bg-slate-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-slate-900" data-testid={`consol-unidades-${g.location_id}`}>{fmtNum(p.total_unidades)}</p><p className="text-xs text-muted-foreground">unidades</p></div>
          </div>
          {p.orders_detail.some((o) => o.status === 'draft') && <p className="text-[11px] text-amber-700">Incluye pedidos en borrador</p>}
          <div className="flex gap-2">
            <Button className="flex-1" onClick={onStart} data-testid={`consol-iniciar-${g.location_id}`}><Truck className="w-4 h-4 mr-2" /> Iniciar Despacho</Button>
            <Button variant="outline" onClick={onPrint} data-testid={`consol-imprimir-${g.location_id}`}><Printer className="w-4 h-4" /></Button>
          </div>
        </>
      )}
      {g.despachos.length > 0 && (
        <div className="space-y-1.5">
          {g.despachos.slice().reverse().slice(0, verTodos ? undefined : 2).map((d) => <DespachoHecho key={d.id} d={d} onEdit={() => onEdit(d)} />)}
          {g.despachos.length > 2 && (
            <button type="button" onClick={() => setVerTodos(!verTodos)} className="text-xs text-primary font-medium" data-testid={`consol-ver-todos-${g.location_id}`}>
              {verTodos ? 'Ver menos' : `Ver los ${g.despachos.length} despachos del día`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const PedidosPorLocal = ({ date, warehouseId, onPrint, onChanged }) => {
  const [grupos, setGrupos] = useState(null);
  const [dialog, setDialog] = useState(null);
  const load = useCallback(() => consolidadoAPI.list(date, warehouseId).then((r) => setGrupos(r.data)), [date, warehouseId]);
  useEffect(() => { load(); }, [load]);

  if (!grupos) return <p className="text-slate-500">Cargando...</p>;
  if (!grupos.length) return (
    <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
      <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-slate-900">No hay pedidos para esta fecha</h3>
    </div>
  );
  const pendientes = grupos.filter((g) => g.pendiente).length;
  return (
    <div className="space-y-4" data-testid="consol-pedidos-por-local">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Un despacho por local</h2>
        <span className="text-sm text-muted-foreground">{grupos.length} locales · {pendientes} por despachar</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {grupos.map((g) => (
          <LocalCard key={g.location_id} g={g}
            onStart={() => setDialog({ local: g, despacho: null })}
            onEdit={(d) => setDialog({ local: g, despacho: d })}
            onPrint={() => onPrint({ id: `Consolidado (${g.pendiente.orders_detail.map((o) => o.maestro).join(', ')})`, location_name: g.location_name, created_at: new Date().toISOString(), items: g.pendiente.items })} />
        ))}
      </div>
      <DespachoConsolidadoDialog open={!!dialog} local={dialog?.local} despacho={dialog?.despacho} orderDate={date}
        onClose={() => setDialog(null)} onDone={() => { load(); onChanged && onChanged(); }} />
    </div>
  );
};
